/**
 * Modal para crear o editar regla de límite.
 *
 * @taskId T479
 * @see functional/Specs/specs_cycle_4.md HU-28-FE §Pantalla 2, §Pantalla 3
 */

import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@flexibilitypay/ui';

import { useCreateLimitRule, useUpdateLimitRule } from '../hooks/use-limits';
import type {
  CreateLimitRulePayload,
  LimitRuleSummary,
  LimitSubjectType,
  LimitOperationType,
  LimitPeriodScope,
} from '../types/limit.types';
import {
  isValidLimit,
  SUBJECT_TYPE_LABELS,
  PERIOD_SCOPE_LABELS,
} from '../types/limit.types';

export interface LimitRuleFormModalProps {
  /** Regla a editar (null para crear) */
  rule: LimitRuleSummary | null;
  open: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
}

export function LimitRuleFormModal({
  rule,
  open,
  onCancel,
  onSuccess,
}: LimitRuleFormModalProps) {
  const formKey = rule?.id ?? 'create';

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onCancel()}>
      <DialogContent className="max-w-md">
        {open ? (
          <LimitRuleFormBody
            key={formKey}
            rule={rule}
            onCancel={onCancel}
            onSuccess={onSuccess}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

interface LimitRuleFormBodyProps {
  rule: LimitRuleSummary | null;
  onCancel: () => void;
  onSuccess?: () => void;
}

function LimitRuleFormBody({ rule, onCancel, onSuccess }: LimitRuleFormBodyProps) {
  const { t } = useTranslation(['limits', 'common']);
  const isEditing = rule !== null;

  // Form state — inicializado desde LimitRuleSummary (view-model FE)
  const [subjectType, setSubjectType] = useState<LimitSubjectType>(rule?.aplicaA ?? 'ALL');
  const [operationType] = useState<LimitOperationType>('TRANSFER');
  // Defaults para creación: DAILY (spec §Pantalla 3)
  const [scope, setScope] = useState<LimitPeriodScope>(rule?.periodo ?? 'DAILY');
  // Default pspLimit: 1000 (spec §Pantalla 3)
  const [pspLimit, setPspLimit] = useState(String(rule?.monto ?? 1000));
  const [regulatoryLimit, setRegulatoryLimit] = useState(rule?.limiteNormativo ?? '');
  // nombre mapea a description del BE
  const [nombre, setNombre] = useState(rule?.nombre ?? '');
  // activo: default true para creación; editable en edición (Matriz editabilidad §T479)
  const [activo, setActivo] = useState(rule?.activo ?? true);
  const [formError, setFormError] = useState('');

  // Mutations
  const createMutation = useCreateLimitRule();
  const updateMutation = useUpdateLimitRule();
  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError('');

      // Validación: monto mayor a 0 (spec §Pantalla 2 §Pantalla 3)
      if (!pspLimit || !String(pspLimit).trim()) {
        setFormError(t('limits:form.errors.limiteRequired', 'El límite operativo es obligatorio'));
        return;
      }
      if (!isValidLimit(pspLimit, regulatoryLimit || undefined)) {
        setFormError(
          t('limits:form.errors.limiteInvalid', 'El monto debe ser mayor a 0'),
        );
        return;
      }

      // Validación: nombre no vacío (spec §Pantalla 2 §Pantalla 3)
      if (!nombre.trim()) {
        setFormError(t('limits:form.errors.nameRequired', 'El nombre es requerido'));
        return;
      }

      try {
        if (isEditing && rule) {
          await updateMutation.mutateAsync({
            id: rule.id,
            payload: {
              pspLimit,
              regulatoryLimit: regulatoryLimit || undefined,
              description: nombre || undefined,
              active: activo,
            },
          });
        } else {
          const payload: CreateLimitRulePayload = {
            subjectType,
            operationType,
            scope,
            pspLimit,
            regulatoryLimit: regulatoryLimit || undefined,
            description: nombre || undefined,
          };
          await createMutation.mutateAsync(payload);
        }
        onSuccess?.();
        onCancel();
      } catch {
        setFormError(
          isEditing
            ? t('limits:form.errors.updateFailed', 'Error al actualizar la regla')
            : t('limits:form.errors.createFailed', 'Error al crear la regla'),
        );
      }
    },
    [
      pspLimit,
      regulatoryLimit,
      nombre,
      activo,
      subjectType,
      operationType,
      scope,
      rule,
      isEditing,
      createMutation,
      updateMutation,
      onCancel,
      onSuccess,
      t,
    ],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isEditing
            ? t('limits:form.editTitle', 'Editar Regla de Límite')
            : t('limits:form.createTitle', 'Crear Nueva Regla de Límite')}
        </DialogTitle>
        <DialogDescription>
          {isEditing
            ? t('limits:form.editDescription', 'Modifique los campos editables.')
            : t('limits:form.createDescription', 'Configure una nueva regla de límite transaccional.')}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Nombre (mapea a description en BE) */}
        <div className="space-y-2">
          <Label htmlFor="nombre">
            {t('limits:form.name', 'Nombre')}
            <span className="text-destructive ml-1">*</span>
          </Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder={t('limits:form.namePlaceholder', 'Ej: Límite diario personas físicas')}
            maxLength={255}
          />
        </div>

        {/* Aplica a (subjectType — inmutable en edición) */}
        <div className="space-y-2">
          <Label htmlFor="subjectType">{t('limits:form.subjectType', 'Aplica a')}</Label>
          <Select
            value={subjectType}
            onValueChange={(v: string) => setSubjectType(v as LimitSubjectType)}
            disabled={isEditing}
          >
            <SelectTrigger id="subjectType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(SUBJECT_TYPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isEditing && (
            <p className="text-xs text-muted-foreground">
              {t('limits:form.subjectTypeImmutable', 'No editable después de la creación')}
            </p>
          )}
        </div>

        {/* Tipo de Transacción (operationType — siempre TRANSFER en MVP, inmutable; TRANSFER → "Todas" per spec §Pantalla 3) */}
        <div className="space-y-2">
          <Label htmlFor="operationType">{t('limits:form.operationType', 'Tipo de Transacción')}</Label>
          <Select value={operationType} disabled>
            <SelectTrigger id="operationType">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {/* MVP: TRANSFER mapeado a "Todas" (spec §Pantalla 3) */}
              <SelectItem value="TRANSFER">{t('limits:form.operationTypeAll', 'Todas')}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t('limits:form.operationTypeMvp', 'Solo transferencias en MVP')}
          </p>
        </div>

        {/* Período (scope — inmutable en edición) */}
        <div className="space-y-2">
          <Label htmlFor="scope">{t('limits:form.scope', 'Período')}</Label>
          <Select
            value={scope}
            onValueChange={(v: string) => setScope(v as LimitPeriodScope)}
            disabled={isEditing}
          >
            <SelectTrigger id="scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(PERIOD_SCOPE_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isEditing && (
            <p className="text-xs text-muted-foreground">
              {t('limits:form.scopeImmutable', 'No editable después de la creación')}
            </p>
          )}
        </div>

        {/* Monto Límite (pspLimit del BE) */}
        <div className="space-y-2">
          <Label htmlFor="pspLimit">
            {t('limits:form.pspLimit', 'Monto Límite')}
            <span className="text-destructive ml-1">*</span>
          </Label>
          <Input
            id="pspLimit"
            type="number"
            min="1"
            step="1"
            value={pspLimit}
            onChange={(e) => setPspLimit(e.target.value)}
            placeholder="1000"
          />
        </div>

        {/* Moneda (display-only — MVP solo ARS) */}
        <div className="space-y-2">
          <Label htmlFor="currency">{t('limits:form.currency', 'Moneda')}</Label>
          <Select value="ARS" disabled>
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ARS">ARS — Peso argentino</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {t('limits:form.currencyMvp', 'Solo ARS disponible en MVP')}
          </p>
        </div>

        {/* Límite normativo (opcional) */}
        <div className="space-y-2">
          <Label htmlFor="regulatoryLimit">
            {t('limits:form.regulatoryLimit', 'Límite normativo')}
            <span className="text-muted-foreground ml-1 text-xs">{t('common:optional', '(opcional)')}</span>
          </Label>
          <Input
            id="regulatoryLimit"
            type="number"
            min="1"
            step="1"
            value={regulatoryLimit}
            onChange={(e) => setRegulatoryLimit(e.target.value)}
            placeholder="10000000"
          />
        </div>

        {/* Regla activa (toggle — editable en edición y creación) */}
        <div className="flex items-center justify-between rounded-lg border p-3">
          <div className="space-y-0.5">
            <Label htmlFor="active" className="text-sm font-medium cursor-pointer">
              {t('limits:form.active', 'Regla activa')}
            </Label>
            <p className="text-xs text-muted-foreground">
              {activo
                ? t('limits:form.activeOn', 'La regla se aplicará a las transacciones')
                : t('limits:form.activeOff', 'La regla no se aplicará (desactivada)')}
            </p>
          </div>
          <Switch
            id="active"
            checked={activo}
            onCheckedChange={setActivo}
          />
        </div>

        {/* Error */}
        {formError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common:cancel', 'Cancelar')}
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {t('common:save', 'Guardar')}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}
